import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from 'modules/logger';
import { getPageType } from 'utils/pageTypes';
import { getPlaceableScreenName } from './utils';

const OWNER_LOCK_NAME = 'srna-workspace-layout-auto-apply';

// One window per origin owns auto-apply; the browser hands the lock to the next window when it closes.
// Only windows showing a screen layouts can place may own it, so hidden or login pages never block it.
export function useIsWorkspaceLayoutOwner({ isEnabled }: { isEnabled: boolean }): boolean {
  const { pathname } = useLocation();
  const canOwn = isEnabled && getPlaceableScreenName(pathname) != null;
  const [hasLock, setHasLock] = useState(false);

  useEffect(() => {
    if (!canOwn || !isWebLocksSupported()) return;

    const abortController = new AbortController();
    let isCleanedUp = false;
    let releaseLock = () => {};

    navigator.locks
      .request(OWNER_LOCK_NAME, { signal: abortController.signal }, () => {
        // Granted after this effect was cleaned up, so hand the lock straight back
        if (isCleanedUp) return Promise.resolve();

        setHasLock(true);
        logger.info('[WorkspaceLayoutManager] This window now owns layout auto-apply', {
          pageType: getPageType(window.location.pathname),
        });
        return new Promise<void>((resolve) => {
          releaseLock = resolve;
        });
      })
      .catch((error: unknown) => {
        // Expected when a waiting window closes before it gets the lock
        if (error instanceof DOMException && error.name === 'AbortError') return;
        logger.warn('[WorkspaceLayoutManager] Could not request the layout owner lock', error);
      });

    return () => {
      isCleanedUp = true;
      abortController.abort();
      releaseLock();
      setHasLock(false);
    };
  }, [canOwn]);

  if (!canOwn) return false;

  return isWebLocksSupported() ? hasLock : true;
}

function isWebLocksSupported(): boolean {
  return typeof navigator !== 'undefined' && navigator.locks != null;
}
